import React, { useState, useEffect } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { trackCookieConsent } from '../services/analytics';
import {
  Box,
  Container,
  HStack,
  VStack,
  Text,
  Button,
  Link,
  Icon,
  Collapsible,
  Checkbox,
  IconButton,
  Separator,
} from '@chakra-ui/react';
import { FaCookieBite, FaTimes, FaCog } from 'react-icons/fa';

const COOKIE_CONSENT_KEY = 'cadencya_cookie_consent';

// Actualizar consentimiento de Google Analytics (fuera del componente, no depende de estado)
const updateGtagConsent = (prefs) => {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'analytics_storage': prefs.analytics ? 'granted' : 'denied',
      'ad_storage': prefs.marketing ? 'granted' : 'denied',
      'ad_user_data': prefs.marketing ? 'granted' : 'denied',
      'ad_personalization': prefs.marketing ? 'granted' : 'denied',
    });
  }
};

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Siempre activas, no se pueden desactivar
    analytics: false,
    marketing: false,
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const settingsBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    // Comprobar si ya existe consentimiento
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) {
      // Mostrar banner si no hay consentimiento previo
      setIsVisible(true);
    } else {
      // Cargar preferencias guardadas y aplicar a gtag
      try {
        const parsed = JSON.parse(savedConsent);
        setPreferences(parsed);
        updateGtagConsent(parsed);
      } catch (e) {
        setIsVisible(true);
      }
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setIsVisible(false);
    updateGtagConsent(prefs);
    trackCookieConsent(prefs.analytics, prefs.marketing);
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectNonEssential = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  if (!isVisible) return null;

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      zIndex={9999}
      bg={bgColor}
      borderTop="1px solid"
      borderColor={borderColor}
      boxShadow="0 -4px 20px rgba(0, 0, 0, 0.1)"
    >
      <Container maxW="7xl" py={4}>
        <VStack gap={4} align="stretch">
          {/* Contenido principal */}
          <HStack 
            gap={{ base: 3, md: 6 }} 
            align="flex-start"
            flexDir={{ base: 'column', md: 'row' }}
          >
            {/* Icono */}
            <Icon
              w={8}
              h={8}
              color="orange.400"
              flexShrink={0}
              display={{ base: 'none', md: 'block' }}
              asChild><FaCookieBite /></Icon>

            {/* Texto */}
            <VStack align="start" gap={1} flex={1}>
              <Text fontWeight="bold" fontSize="md" color={textColor}>
                🍪 Utilizamos cookies
              </Text>
              <Text fontSize="sm" color={textColor}>
                Usamos cookies propias y de terceros para mejorar tu experiencia, 
                analizar el tráfico y personalizar contenido. Puedes aceptar todas, 
                rechazar las no esenciales, o configurar tus preferencias.{' '}
                <Link 
                  href="/politica-cookies" 
                  color="purple.500" 
                  fontWeight="medium"
                  textDecoration="underline"
                >
                  Más información
                </Link>
              </Text>
            </VStack>

            {/* Botones principales */}
            <HStack 
              gap={2} 
              flexShrink={0}
              flexWrap="wrap"
              justify={{ base: 'stretch', md: 'flex-end' }}
              w={{ base: 'full', md: 'auto' }}
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowSettings(!showSettings)}
                flex={{ base: 1, md: 'none' }}><Icon asChild><FaCog /></Icon>Configurar
                              </Button>
              <Button
                size="sm"
                variant="outline"
                colorPalette="gray"
                onClick={handleRejectNonEssential}
                flex={{ base: 1, md: 'none' }}
              >
                Solo esenciales
              </Button>
              <Button
                size="sm"
                colorPalette="purple"
                onClick={handleAcceptAll}
                flex={{ base: 1, md: 'none' }}
              >
                Aceptar todas
              </Button>
            </HStack>
          </HStack>

          {/* Panel de configuración */}
          <Collapsible.Root open={showSettings}>
            <Collapsible.Content>
              <Box 
                p={4} 
                bg={settingsBg} 
                rounded="md"
                mt={2}
              >
                <VStack align="stretch" gap={4}>
                  <Text fontWeight="bold" fontSize="sm">
                    Configuración de cookies
                  </Text>
                  
                  {/* Cookies esenciales */}
                  <HStack justify="space-between" align="start">
                    <VStack align="start" gap={0} flex={1}>
                      <HStack>
                        <Text fontWeight="medium" fontSize="sm">Cookies esenciales</Text>
                        <Text 
                          fontSize="xs" 
                          bg="green.100" 
                          color="green.700" 
                          px={2} 
                          py={0.5} 
                          rounded="full"
                        >
                          Siempre activas
                        </Text>
                      </HStack>
                      <Text fontSize="xs" color="gray.500">
                        Necesarias para el funcionamiento básico del sitio (autenticación, seguridad, preferencias).
                      </Text>
                    </VStack>
                    <Checkbox.Root disabled colorPalette="green" checked={true}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control></Checkbox.Root>
                  </HStack>

                  <Separator />

                  {/* Cookies de analytics */}
                  <HStack justify="space-between" align="start">
                    <VStack align="start" gap={0} flex={1}>
                      <Text fontWeight="medium" fontSize="sm">Cookies de análisis</Text>
                      <Text fontSize="xs" color="gray.500">
                        Nos ayudan a entender cómo usas el sitio para mejorar la experiencia (Google Analytics).
                      </Text>
                    </VStack>
                    <Checkbox.Root
                      onCheckedChange={(details) => setPreferences(prev => ({ ...prev, analytics: details.checked === true }))}
                      colorPalette="purple"
                      checked={preferences.analytics}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control></Checkbox.Root>
                  </HStack>

                  <Separator />

                  {/* Cookies de marketing */}
                  <HStack justify="space-between" align="start">
                    <VStack align="start" gap={0} flex={1}>
                      <Text fontWeight="medium" fontSize="sm">Cookies de marketing</Text>
                      <Text fontSize="xs" color="gray.500">
                        Permiten mostrarte anuncios relevantes en otros sitios web.
                      </Text>
                    </VStack>
                    <Checkbox.Root
                      onCheckedChange={(details) => setPreferences(prev => ({ ...prev, marketing: details.checked === true }))}
                      colorPalette="purple"
                      checked={preferences.marketing}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control></Checkbox.Root>
                  </HStack>

                  <Separator />

                  <HStack justify="flex-end">
                    <Button 
                      size="sm" 
                      colorPalette="purple"
                      onClick={handleSavePreferences}
                    >
                      Guardar preferencias
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            </Collapsible.Content>
          </Collapsible.Root>
        </VStack>
      </Container>
    </Box>
  );
};

// Hook para acceder a las preferencias de cookies desde otros componentes
export const useCookieConsent = () => {
  const [consent, setConsent] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (savedConsent) {
      try {
        setConsent(JSON.parse(savedConsent));
      } catch (e) {
        // Mantener valores por defecto
      }
    }
  }, []);

  return consent;
};

export default CookieBanner;

